import styles from './index.module.css'
import React from 'react'
import type { PlaylistOrigin as PlaylistOriginType } from 'types/playlist'
import ExpandableText from 'components/ui/expandable-text'
import Stack from 'components/ui/stack'
import {
  AccessibleIcon,
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from 'components/ds'
import { formatDateWithTimezone } from '@disco/utils/time-utils'
import { ExternalLinkIcon } from 'views/components/ui/icon/svg-icons'

type Sender = {
  name: string
  email: string
}
type Message = {
  title?: string
  message: string
}
type Props = {
  origin?: PlaylistOriginType
  sender?: Sender
  message?: Message | null | undefined
  showFullMessage?: boolean
  footer?: React.ReactNode
  fromBrief?: {
    id: string
    inboxId: string
    submission: {
      version: number
      versionCreated: string
      isLatestVersion: boolean
    }
  }
}

const PlayListFromBrief = ({ fromBrief }) => {
  if (!fromBrief) return null

  const { submission, id, inboxId } = fromBrief

  const versionLink = `/brief-editor/${id}/${inboxId}/preview?version=${submission.version}`
  const versionLinkText = `Re: Version - ${formatDateWithTimezone(
    submission.versionCreated,
    'UTC',
  )} ${submission.isLatestVersion ? '(Latest)' : ''}`

  return (
    <Flex>
      <a
        className={styles.senderLink}
        href={versionLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Text weight="medium">{versionLinkText}</Text>
      </a>
      <Box ml="auto">
        <Tooltip content={versionLinkText}>
          <IconButton
            variant="ghost"
            color="gray"
            size="2"
            onClick={() => window.open(versionLink, '_blank')}
          >
            <AccessibleIcon label="View Brief">
              <ExternalLinkIcon size={16} />
            </AccessibleIcon>
          </IconButton>
        </Tooltip>
      </Box>
    </Flex>
  )
}

/**
 * PlaylistOriginLogo component for displaying business logo
 *
 * Renders the business logo from the playlist origin data if available.
 *
 * @param props - Component props
 * @param props.origin - Playlist origin data containing business information
 * @returns JSX element containing the logo image, or null if no logo available
 */
const PlaylistOriginLogo = ({
  origin,
}: {
  origin?: PlaylistOriginType
}): React.ReactNode => {
  if (!origin?.business?.logo) return null
  return (
    <div className={styles.logoContainer}>
      <img
        className={styles.logo}
        src={origin.business.logo}
        alt={`${origin.business.name ?? 'Business'} logo`}
      />
    </div>
  )
}

/**
 * PlaylistOriginBusiness component for displaying business name and link
 *
 * Renders the business name, optionally as a link to the source or primary library.
 *
 * @param props - Component props
 * @param props.origin - Playlist origin data containing business and library information
 * @returns JSX element containing the business name/link, or null if no business name
 */
const PlaylistOriginBusiness = ({
  origin,
}: {
  origin?: PlaylistOriginType
}): React.ReactNode => {
  if (!origin?.business?.name) return null
  // Company name links out to primary catalog or company name links out to catalog from where the playlist was saved
  const libraryUrl = origin.source_library?.url || origin.primary_library?.url

  if (libraryUrl && origin.business?.name) {
    return (
      <div className={styles.senderBusiness}>
        <a href={libraryUrl} target="_blank" rel="noopener noreferrer">
          {origin.business.name}
        </a>
      </div>
    )
  }

  return (
    <div className={styles.senderBusiness}>
      {origin?.business?.name ?? null}
    </div>
  )
}

/**
 * PlaylistSender component for displaying sender information
 *
 * Renders the sender's name and email as a mailto link.
 *
 * @param props - Component props
 * @param props.sender - Sender object containing name and email
 * @returns JSX element containing the sender mailto link, or null if no sender
 */
const PlaylistSender = ({
  sender,
}: {
  sender: Sender | null | undefined
}): React.ReactNode => {
  if (!sender) return null
  return (
    <a className={styles.senderLink} href={`mailto:${sender.email}`}>
      {sender.name ? (
        <span className={styles.senderName}>{sender.name}&nbsp;</span>
      ) : null}
      <span className={styles.senderEmail}>{sender.email}</span>
    </a>
  )
}

/**
 * PlaylistMessage component for displaying playlist message
 *
 * Renders the message title and content, with support for expandable text.
 *
 * @param props - Component props
 * @param props.message - Message object containing title and message content
 * @param props.showFullMessage - Whether to show full message or use expandable text
 * @returns JSX element containing the message, or null if no message
 */
const PlaylistMessage = ({
  message,
  showFullMessage,
}: {
  message: Message | null | undefined
  showFullMessage?: boolean
}): React.ReactNode => {
  if (!message) return null
  return (
    <div className={styles.message}>
      {Boolean(message.title) && (
        <div className={styles.title}>{message.title}</div>
      )}
      {showFullMessage ? (
        <span>{message.message}</span>
      ) : (
        <ExpandableText text={message.message} visibleLines={5} />
      )}
    </div>
  )
}

/**
 * PlaylistOrigin component for displaying playlist origin information
 *
 * A comprehensive component that displays playlist origin details including business logo,
 * business name/link, sender information, and optional message. Combines multiple
 * sub-components to create a complete origin card.
 *
 * @param props - Component props
 * @param props.origin - Playlist origin data containing business and library information
 * @param props.sender - Optional sender information with name and email
 * @param props.message - Optional message with title and content
 * @param props.showFullMessage - Whether to show full message or use expandable text
 * @param props.footer - Optional footer content to display below the message
 * @returns JSX element containing the complete origin information, or null if no content
 */
export const PlaylistOrigin = ({
  origin,
  sender,
  message,
  showFullMessage,
  footer,
  fromBrief,
}: Props): React.ReactNode => {
  const emptyOrigin = !origin?.business?.name
  const hasBusiness = Boolean(origin?.business?.name)
  if (emptyOrigin && !sender && !message) return null
  return (
    <Flex direction="column" className={styles.container} gap="1">
      <PlayListFromBrief fromBrief={fromBrief} />
      <Flex>
        {<PlaylistOriginLogo origin={origin} />}
        <div className={styles.sender}>
          {/* When business name exists: show "From: Business" on first line, name+email on second line */}
          {/* When no business name: show "From: name email" all on same line */}
          {hasBusiness && sender ? (
            <>
              <div className={styles.from}>
                <div className={styles.fromLabel}>From:&nbsp;</div>
                <PlaylistOriginBusiness origin={origin} />
              </div>
              <PlaylistSender sender={sender} />
            </>
          ) : sender ? (
            <div className={styles.from}>
              <div className={styles.fromLabel}>From:&nbsp;</div>
              <PlaylistSender sender={sender} />
            </div>
          ) : (
            <>
              <div className={styles.from}>
                <div className={styles.fromLabel}>From:&nbsp;</div>
              </div>
              <PlaylistOriginBusiness origin={origin} />
            </>
          )}
        </div>
      </Flex>
      <Stack space="xsmall">
        <PlaylistMessage message={message} showFullMessage={showFullMessage} />
        {footer}
      </Stack>
    </Flex>
  )
}
